
import React from "react";
import { BarChart3, CalendarClock, ShieldCheck, Award } from "lucide-react";
import { ExtendedOrganization } from "@/hooks/organization-settings/types";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useNavigate } from "react-router-dom";

interface SubscriptionStatusCardProps {
  organization: ExtendedOrganization | null;
}

export const SubscriptionStatusCard: React.FC<SubscriptionStatusCardProps> = ({
  organization
}) => {
  console.log("SubscriptionStatusCard rendering with organization:", organization);
  console.log("Trial ends at:", organization?.trial_ends_at);
  console.log("Next billing date:", organization?.next_billing_date);
  console.log("Subscription status:", organization?.subscription_status);
  
  const navigate = useNavigate();
  
  if (!organization) {
    return (
      <Card className="mb-8 p-6 border shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((index) => (
            <div key={index} className="flex flex-col items-center text-center">
              <Skeleton className="w-12 h-12 rounded-full mb-2" />
              <Skeleton className="h-4 w-32 mb-1" />
              <Skeleton className="h-5 w-24" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  const planDisplayNames: Record<string, string> = {
    'standard': 'Standard',
    'professional': 'Professionnel',
    'enterprise': 'Enterprise'
  };

  const planName = organization?.plan 
    ? planDisplayNames[organization.plan.toLowerCase()] || organization.plan
    : "Standard";
  
  let expirationDate = "∞";
  
  if (organization.next_billing_date) {
    try {
      const billingDate = new Date(organization.next_billing_date);
      expirationDate = format(billingDate, 'dd MMMM yyyy', { locale: fr });
      console.log("Formatted billing date:", expirationDate);
    } catch (error) {
      console.error("Error formatting billing date:", error);
    }
  } else if (organization.trial_ends_at) {
    try {
      const trialEndDate = new Date(organization.trial_ends_at);
      expirationDate = format(trialEndDate, 'dd MMMM yyyy', { locale: fr });
      console.log("Formatted trial end date:", expirationDate);
    } catch (error) {
      console.error("Error formatting trial end date:", error);
    }
  }
  
  const accountStatus = organization.is_active ? "Actif" : "Inactif";
  const statusVariant = organization.is_active ? "success" : "destructive";
  
  const isTrial = organization.trial_ends_at ? true : false;
  const isTrialing = organization.subscription_status === "trialing";

  const isProfessional = organization?.plan?.toLowerCase() === 'professional';
  const isEnterprise = organization?.plan?.toLowerCase() === 'enterprise';

  const getBadgeColor = () => {
    if (isEnterprise) return "bg-indigo-100 text-indigo-800 border-indigo-200";
    if (isProfessional) return "bg-purple-100 text-purple-800 border-purple-200";
    return "bg-blue-100 text-blue-700 border-blue-200";
  };

  const getIconBgColor = () => {
    if (isEnterprise) return "bg-gradient-to-br from-indigo-50 to-indigo-100";
    if (isProfessional) return "bg-gradient-to-br from-purple-50 to-purple-100";
    return "bg-gradient-to-br from-blue-50 to-blue-100";
  };

  return (
    <Card className="mb-8 overflow-hidden border-0 shadow-lg rounded-2xl bg-gradient-to-br from-white to-gray-50 dark:from-gray-800/60 dark:to-gray-900/40 dark:border-gray-700/50 transition-all duration-300 hover:shadow-xl">
      <div className="relative">
        {(isProfessional || isEnterprise) && (
          <div className="absolute top-3 right-3">
            <Badge className={`px-3 py-1 flex items-center gap-1 ${isProfessional ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300'}`}>
              <Award className="w-3.5 h-3.5" />
              <span className="font-medium">{isProfessional ? 'Pro' : 'Enterprise'}</span>
            </Badge>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 divide-y md:divide-y-0 md:divide-x divide-gray-100 dark:divide-gray-700/50">
        <div className="flex flex-col items-center text-center p-6 group hover:bg-gray-50/50 dark:hover:bg-gray-800/40 transition-colors duration-300">
          <div className={`${getIconBgColor()} dark:bg-gray-700/30 rounded-full p-4 mb-4 shadow-inner group-hover:scale-110 transition-transform duration-300`}>
            <BarChart3 className={`w-7 h-7 ${isEnterprise ? 'text-indigo-600 dark:text-indigo-400' : isProfessional ? 'text-purple-600 dark:text-purple-400' : 'text-blue-600 dark:text-blue-400'}`} />
          </div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Plan Actuel</h3>
          <div className="flex flex-col items-center">
            <p className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-1">{planName}</p>
            {(isTrial || isTrialing) && (
              <Badge variant="secondary" className="mt-1 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 hover:bg-amber-200 font-medium px-2.5 py-0.5 text-xs rounded-full">
                Version d'essai
              </Badge>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center text-center p-6 group hover:bg-gray-50/50 dark:hover:bg-gray-800/40 transition-colors duration-300">
          <div className="bg-gradient-to-br from-pink-50 to-pink-100 dark:bg-gray-700/30 rounded-full p-4 mb-4 shadow-inner group-hover:scale-110 transition-transform duration-300">
            <CalendarClock className="w-7 h-7 text-pink-600 dark:text-pink-400" />
          </div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
            {organization.next_billing_date ? "Prochaine facturation" : "Date d'expiration"}
          </h3>
          <p className="text-xl font-bold text-gray-800 dark:text-gray-200">{expirationDate}</p>
        </div>

        <div className="flex flex-col items-center text-center p-6 group hover:bg-gray-50/50 dark:hover:bg-gray-800/40 transition-colors duration-300">
          <div className="bg-gradient-to-br from-green-50 to-green-100 dark:bg-gray-700/30 rounded-full p-4 mb-4 shadow-inner group-hover:scale-110 transition-transform duration-300">
            <ShieldCheck className="w-7 h-7 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Statut du compte</h3>
          <Badge variant={statusVariant} className="px-4 py-1 rounded-full font-medium mt-1 text-sm">
            {accountStatus}
          </Badge>
        </div>
      </div>
    </Card>
  );
};
